const ELEMENT_ID = "klaviyo-bis";

const attributes = {
  body: `data-${ELEMENT_ID}-body`,
  close: `data-${ELEMENT_ID}-close`,
  open: `data-${ELEMENT_ID}-open`,
  requestUrl: `data-${ELEMENT_ID}-url`,
  form: `data-${ELEMENT_ID}-form`,
  submitText: `data-${ELEMENT_ID}-submit-text`,
  spinner: `data-${ELEMENT_ID}-spinner`,
  error: `data-${ELEMENT_ID}-error`,
  success: `data-${ELEMENT_ID}-success`,
};

class KlaviyoBIS extends HTMLElement {
  constructor() {
    super();
    this.$dialog = this.querySelector("dialog");
    this.$formElement = this.querySelector(`[${attributes.form}]`);
    this.$closeElements = this.querySelectorAll(`[${attributes.close}]`);
    if (!this.$formElement) throw new Error("[KlaviyoBIS] Form not found");
  }

  connectedCallback() {
    this.#submitForm();
    if (this.$dialog) {
      this.$dialog.addEventListener("close", () => this.#reset());
    } else {
      this.#setCloseEvent();
    }
    document.addEventListener(
      "click",
      (event) => this.#setOpenEvent(event),
      false
    );
  }

  async #setOpenEvent(event) {
    const $openElement = event.target.closest(`[${attributes.open}]`);
    if (!$openElement) return;

    event.preventDefault();

    if (this.$dialog) {
      this.$dialog.showModal();
    } else {
      this.#toggle(true);
    }

    const form = $openElement.closest("form");
    const requestURL = $openElement.getAttribute(attributes.requestUrl);
    const selectedVariantId = form?.querySelector("[name='id']")?.value || 0;

    const url = `${requestURL}${requestURL.indexOf("?") > -1 ? "&" : "?"}${
      parseInt(selectedVariantId) > 0 ? `variant=${selectedVariantId}&` : ""
    }section_id=${ELEMENT_ID}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("[KlaviyoBIS] Response error");

      const html = await response.text();
      this.#replaceHTML(html);
    } catch (error) {
      throw new Error(`[KlaviyoBIS] ${error}`);
    }
  }

  #replaceHTML(html) {
    const newDocument = new DOMParser().parseFromString(html, "text/html");
    const $newElement = newDocument.querySelector(
      `${ELEMENT_ID}${this.id ? "#" + this.id : ""}`
    );

    if (!$newElement) throw new Error("[KlaviyoBIS] Element not found");
    this.querySelector(`[${attributes.body}]`).innerHTML =
      $newElement.querySelector(`[${attributes.body}]`).innerHTML;
  }

  async #submitForm() {
    this.$formElement.addEventListener("submit", async (event) => {
      event.preventDefault();
      this.#reset();
      this.#loading(true);
      try {
        const formData = new FormData(this.$formElement);
        const formData_variantId = formData.get("variant");
        const formData_email = formData.get("email");
        const formData_companyID = this.$formElement.getAttribute(
          "data-klaviyo_company_id"
        );
        const acceptMarketing = formData.get("accept_newsletter") === "1";
        const marketingListId = (
          this.$formElement.getAttribute("data-klaviyo_newsletter_list_id") || ""
        ).trim();

        if (acceptMarketing && marketingListId) {
          this.#sendMarketingRequest(
            formData_companyID,
            formData_email,
            marketingListId
          );
        }

        const payload = {
          data: {
            type: "back-in-stock-subscription",
            attributes: {
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email: formData_email,
                  },
                },
              },
              channels: ["EMAIL"],
            },
            relationships: {
              variant: {
                data: {
                  type: "catalog-variant",
                  id: "$shopify:::$default:::" + formData_variantId,
                },
              },
            },
          },
        };
        const response = await fetch(this.$formElement.action, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            revision: "2023-09-27",
          },
          method: "post",
          body: JSON.stringify(payload),
        });
        if (response?.ok) {
          this.#success(true);
        } else {
          this.#error(true);
        }

        this.#loading(false);
      } catch (error) {
        this.#loading(false);
        throw new Error(`[KlaviyoBIS] ${error}`);
      }
    });
  }

  async #sendMarketingRequest(companyId, email, listId) {
    try {
      const payload = {
        data: {
          type: "subscription",
          attributes: {
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: email,
                },
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: "list",
                id: listId,
              },
            },
          },
        },
      };

      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: "2025-01-15",
        },
        body: JSON.stringify(payload),
      };

      const url = new URL("https://a.klaviyo.com/client/subscriptions/");
      url.searchParams.append("company_id", companyId);

      const response = await fetch(url, requestOptions);
      if (!response.ok) {
        throw new Error("Marketing request failed");
      }
    } catch (error) {
      console.error(error);
    }
  }

  #setCloseEvent() {
    document.addEventListener("keyup", (event) => {
      if (event.code?.toUpperCase() === "ESCAPE") this.#toggle(false);
    });
    if (this.$closeElements?.length) {
      this.$closeElements.forEach((element) =>
        element.addEventListener("click", (e) => {
          e.preventDefault();
          this.#toggle(false);
        })
      );
    }
  }

  #toggle(open) {
    this.#reset();
    open ? this.setAttribute("open", "") : this.removeAttribute("open");
  }

  #loading(activate) {
    activate
      ? this.setAttribute("loading", "")
      : this.removeAttribute("loading");
  }

  #success(activate) {
    activate
      ? this.setAttribute("success", "")
      : this.removeAttribute("success");
  }

  #error(activate) {
    activate ? this.setAttribute("error", "") : this.removeAttribute("error");
  }

  #reset() {
    this.#error(false);
    this.#success(false);
    this.#loading(false);
  }
}
customElements.define(ELEMENT_ID, KlaviyoBIS);
